import { useEffect, useRef, useState } from "react";
import { Card, Form, Offcanvas } from "react-bootstrap";
import { DashSquareFill, PlusSquareFill } from "react-bootstrap-icons";
import { useForm } from "react-hook-form";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import CustomButton from "../../components/CustomButton/CustomButton";
import CommonQuill from "../../components/editor/CommonQuill";
import HelperService from "../../utility/HelperService";

interface AddBessForCIProps {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

type FormData = {
  title: string;
  slug: string;
  createdBy: string;
  meta_description: string;
  keyword: string;

  registration_button_text: string;
  registration_link: string;

  launch_title: string;
  launch_subtitle: string;

  core_focus_title: string;
};

type Block = {
  title: string;
  description?: string;
};

const EMPTY_BLOCK: Block = { title: "", description: "" };

// "Who is this for?" always shows exactly two cards, so it is not add/remove-able.
const AUDIENCE_COUNT = 2;

const toFixedLength = (list: Block[] = [], count: number) =>
  Array.from({ length: count }, (_, i) => list[i] ?? { ...EMPTY_BLOCK });

const AddBessForCI = ({ show, handleClose, data }: AddBessForCIProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    trigger,
  } = useForm<FormData>();

  const [logo, setLogo] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [coreFocusDescription, setCoreFocusDescription] = useState("");
  const [coreFocusShortDescription, setCoreFocusShortDescription] =
    useState("");
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(true);

  const [audiences, setAudiences] = useState<Block[]>(
    toFixedLength([], AUDIENCE_COUNT)
  );
  const [criteria, setCriteria] = useState<Block[]>([{ ...EMPTY_BLOCK }]);
  const [values, setValues] = useState<Block[]>([{ ...EMPTY_BLOCK }]);

  const descriptionQuillRef = useRef<any>(null);
  const shortDescQuillRef = useRef<any>(null);
  const coreFocusQuillRef = useRef<any>(null);
  const coreFocusShortQuillRef = useRef<any>(null);

  const [customErrors, setCustomErrors] = useState<{
    description?: string;
    short_description?: string;
  }>({});
  const [isLoading, setLoading] = useState(false);

  const isQuillEmpty = (value?: string) => {
    if (!value) return true;
    const text = value
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();
    return text.length === 0;
  };

  const validateInfoTab = async () => {
    const titleValid = await trigger(["title"]);

    const newErrors: any = {};
    if (isQuillEmpty(description)) {
      newErrors.description = "Description is required";
    }
    if (isQuillEmpty(shortDescription)) {
      newErrors.short_description = "Short Description is required";
    }

    setCustomErrors(newErrors);

    return titleValid && !newErrors.description && !newErrors.short_description;
  };

  // ---------- SLUG AUTO GENERATE (add mode only) ----------
  const titleValue = watch("title");
  useEffect(() => {
    if (data?._id) return;

    if (!titleValue) {
      setValue("slug", "");
      return;
    }

    setValue("slug", HelperService.generateSlug(titleValue));
  }, [titleValue, data]);

  // ---------- PREFILL ON EDIT ----------
  useEffect(() => {
    if (data) {
      const storedUser = localStorage.getItem("UserData");
      const userData = storedUser ? JSON.parse(storedUser) : null;

      reset({
        title: data.title,
        slug: data.slug,
        keyword: data?.keyword,
        createdBy: userData?.fullName || "",
        meta_description: data?.meta_description,
        registration_button_text:
          data?.registration_button_text || "Registration is Open",
        registration_link: data?.registration_link || "",
        launch_title: data?.launch_title || "",
        launch_subtitle: data?.launch_subtitle || "",
        core_focus_title:
          data?.core_focus_title || "Core Focus of BESS for C&I Workgroup",
      });

      setLogo(null);
      setDescription(data.description || "");
      setShortDescription(data.short_description || "");
      setCoreFocusDescription(data.core_focus_description || "");
      setCoreFocusShortDescription(data.core_focus_short_description || "");
      setIsRegistrationOpen(data.is_registration_open !== false);

      setAudiences(toFixedLength(data.audiences, AUDIENCE_COUNT));
      setCriteria(
        data.criteria?.length > 0 ? data.criteria : [{ ...EMPTY_BLOCK }]
      );
      setValues(data.values?.length > 0 ? data.values : [{ ...EMPTY_BLOCK }]);
    }
  }, [data, show]);

  // ---------- REUSABLE REPEATER ----------
  const renderBlockRepeater = (
    sectionLabel: string,
    list: Block[],
    setList: (v: Block[]) => void,
    dragKey: string,
    options: {
      withDescription?: boolean;
      placeholder?: string;
      fixed?: boolean;
    } = {}
  ) => (
    <Card className="p-3 bg-light my-3 position-relative">
      <span
        className="bg-white position-absolute rounded small"
        style={{ top: "-12px", left: "12px", padding: "0 8px" }}
      >
        {sectionLabel}
      </span>

      {list.map((item, index) => (
        <div
          key={index}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData(dragKey, index.toString());
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            const fromIndex = Number(e.dataTransfer.getData(dragKey));
            if (fromIndex === index) return;
            const updated = [...list];
            const [moved] = updated.splice(fromIndex, 1);
            updated.splice(index, 0, moved);
            setList(updated);
          }}
          style={{ cursor: "move" }}
          className="mb-3"
        >
          <div className="d-flex align-items-start">
            <span
              style={{
                cursor: "grab",
                marginRight: 8,
                display: "flex",
                alignItems: "center",
                fontSize: 20,
                color: "#888",
                marginTop: 10,
              }}
              title="Drag to reorder"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <circle cx="5" cy="6" r="1.5" />
                <circle cx="5" cy="10" r="1.5" />
                <circle cx="5" cy="14" r="1.5" />
                <circle cx="11" cy="6" r="1.5" />
                <circle cx="11" cy="10" r="1.5" />
                <circle cx="11" cy="14" r="1.5" />
              </svg>
            </span>

            <div style={{ flex: 1 }} className="highlight-item">
              <div className="d-flex mt-2">
                <Form.Control
                  type="text"
                  placeholder={options.placeholder || "Title"}
                  value={item.title || ""}
                  onChange={(e) => {
                    const updated = [...list];
                    updated[index].title = e.target.value;
                    setList(updated);
                  }}
                />
                {!options.fixed && (
                  <div className="d-flex mt-2">
                    <PlusSquareFill
                      onClick={() => setList([...list, { ...EMPTY_BLOCK }])}
                      className="ms-2 me-2 cursor-pointer"
                    />
                    {list.length > 1 && (
                      <DashSquareFill
                        onClick={() =>
                          setList(list.filter((_, i) => i !== index))
                        }
                        className="cursor-pointer"
                      />
                    )}
                  </div>
                )}
              </div>

              {options.withDescription && (
                <Form.Control
                  as="textarea"
                  rows={2}
                  className="mt-2"
                  placeholder="Description"
                  value={item.description || ""}
                  onChange={(e) => {
                    const updated = [...list];
                    updated[index].description = e.target.value;
                    setList(updated);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      ))}
    </Card>
  );

  // ---------- SUBMIT ----------
  const onSubmit = async (request: FormData) => {
    const valid = await validateInfoTab();
    if (!valid) {
      toast.error("Please fill required fields");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    const storedUser = localStorage.getItem("UserData");
    const userData = storedUser ? JSON.parse(storedUser) : null;

    if (logo) formData.append("logo", logo);
    if (data?._id) formData.append("id", data._id);
    if (!data?._id && userData?._id) formData.append("createdBy", userData._id);

    formData.append("title", request.title);
    formData.append("slug", request.slug || "");

    const descHtml = descriptionQuillRef.current?.getEditor()?.root.innerHTML || "";
    formData.append("description", descHtml);
    formData.append("short_description", shortDescription);

    formData.append(
      "registration_button_text",
      request.registration_button_text || ""
    );
    formData.append("registration_link", request.registration_link || "");
    formData.append("is_registration_open", String(isRegistrationOpen));

    formData.append("launch_title", request.launch_title || "");
    formData.append("launch_subtitle", request.launch_subtitle || "");

    formData.append("core_focus_title", request.core_focus_title || "");
    const coreFocusHtml =
      coreFocusQuillRef.current?.getEditor()?.root.innerHTML || "";
    formData.append("core_focus_description", coreFocusHtml);
    const coreFocusShortHtml =
      coreFocusShortQuillRef.current?.getEditor()?.root.innerHTML || "";
    formData.append("core_focus_short_description", coreFocusShortHtml);

    const cleanBlocks = (list: Block[], withDescription = false) =>
      list
        .filter((item) => item.title && item.title.trim())
        .map((item) => ({
          title: item.title,
          description: withDescription ? item.description || "" : "",
        }));

    formData.append("audiences", JSON.stringify(cleanBlocks(audiences, true)));
    formData.append("criteria", JSON.stringify(cleanBlocks(criteria)));
    formData.append("values", JSON.stringify(cleanBlocks(values)));

    formData.append("meta_description", request.meta_description);
    formData.append("keyword", request.keyword);

    WebService.fileUploadAPI({
      action: `bess-ci/addContent`,
      body: formData,
    })
      .then((res: any) => {
        toast.success(res.message);
        setLoading(false);
        handleClose();
      })
      .catch((err: any) => {
        setLoading(false);
        toast.error(err.response?.data?.message || "An error occurred");
      });
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{data ? "Edit" : "Add"}</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Form>
          {/* ================= MAIN CONTENT ================= */}
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Main Content
            </span>

            <Form.Group className="mb-2" controlId="title">
              <Form.Label className="label">
                Title <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter title"
                {...register("title", { required: "Title is required" })}
              />
              {errors.title && (
                <div className="error-message">{errors.title.message}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="slugField">
              <Form.Label className="label">Slug</Form.Label>{" "}
              <span className="text-danger">*</span>
              <Form.Control
                type="text"
                placeholder="Auto-generated slug"
                {...register("slug")}
                value={watch("slug") || ""}
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="description">
              <Form.Label className="label">
                Description <span className="text-danger">*</span>
              </Form.Label>
              <CommonQuill
                ref={descriptionQuillRef}
                value={description}
                onChange={setDescription}
                storage="BESS_CI"
                initialHtml={
                  show && data?.description
                    ? HelperService.normalizeResizeHtml(data.description)
                    : undefined
                }
                onClearError={() =>
                  setCustomErrors((p) => ({ ...p, description: undefined }))
                }
              />
              {customErrors.description && (
                <div className="error-message">{customErrors.description}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="shortDescription">
              <Form.Label className="label">
                Short Description <span className="text-danger">*</span>
              </Form.Label>
              <CommonQuill
                ref={shortDescQuillRef}
                value={shortDescription}
                onChange={setShortDescription}
                storage="BESS_CI"
                initialHtml={
                  show && data?.short_description
                    ? HelperService.normalizeResizeHtml(data.short_description)
                    : undefined
                }
                onClearError={() =>
                  setCustomErrors((p) => ({
                    ...p,
                    short_description: undefined,
                  }))
                }
              />
              {customErrors.short_description && (
                <div className="error-message">
                  {customErrors.short_description}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="createdBy">
              <Form.Label className="label">Created By</Form.Label>
              <Form.Control
                type="text"
                placeholder="Created by user"
                {...register("createdBy")}
                value={watch("createdBy") || ""}
                disabled
              />
            </Form.Group>
          </Card>

          {/* ================= REGISTRATION ================= */}
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Registration Button
            </span>

            <Form.Group className="mb-2" controlId="registrationButtonText">
              <Form.Label className="label">Button Text</Form.Label>
              <Form.Control
                type="text"
                placeholder="Registration is Open"
                {...register("registration_button_text")}
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="registrationLink">
              <Form.Label className="label">
                Registration Link (Google Form URL)
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="https://docs.google.com/forms/..."
                {...register("registration_link")}
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="isRegistrationOpen">
              <Form.Check
                type="switch"
                label="Registration is open (turn off to disable the button on the website)"
                checked={isRegistrationOpen}
                onChange={(e) => setIsRegistrationOpen(e.target.checked)}
              />
            </Form.Group>
          </Card>

          {/* ================= LAUNCH STRIP ================= */}
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Launch Details
            </span>

            <Form.Group className="mb-2" controlId="launchTitle">
              <Form.Label className="label">Launch Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Launching in Mumbai"
                {...register("launch_title")}
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="launchSubtitle">
              <Form.Label className="label">Launch Date / Month</Form.Label>
              <Form.Control
                type="text"
                placeholder="This September"
                {...register("launch_subtitle")}
              />
            </Form.Group>

          </Card>

          {/* ================= CORE FOCUS ================= */}
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Core Focus
            </span>

            <Form.Group className="mb-2" controlId="coreFocusTitle">
              <Form.Label className="label">Section Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Core Focus of BESS for C&I Workgroup"
                {...register("core_focus_title")}
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="coreFocusDescription">
              <Form.Label className="label">
                Description
              </Form.Label>
              <CommonQuill
                ref={coreFocusQuillRef}
                value={coreFocusDescription}
                onChange={setCoreFocusDescription}
                storage="BESS_CI"
                initialHtml={
                  show && data?.core_focus_description
                    ? HelperService.normalizeResizeHtml(
                        data.core_focus_description
                      )
                    : undefined
                }
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="coreFocusShortDescription">
              <Form.Label className="label">Short Description</Form.Label>
              <CommonQuill
                ref={coreFocusShortQuillRef}
                value={coreFocusShortDescription}
                onChange={setCoreFocusShortDescription}
                storage="BESS_CI"
                initialHtml={
                  show && data?.core_focus_short_description
                    ? HelperService.normalizeResizeHtml(
                        data.core_focus_short_description
                      )
                    : undefined
                }
              />
            </Form.Group>
          </Card>

          {/* ================= WHO IS THIS FOR ================= */}
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Who is this for?
            </span>

            {renderBlockRepeater(
              "Audience Cards",
              audiences,
              setAudiences,
              "AudienceIndex",
              { withDescription: true, fixed: true }
            )}
          </Card>

          {/* ================= CRITERIA TO JOIN ================= */}
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Criteria to Join the Group
            </span>

            {renderBlockRepeater(
              "Tags",
              criteria,
              setCriteria,
              "CriteriaIndex",
              { placeholder: "Tag e.g. End Consumers" }
            )}
          </Card>

          {/* ================= VALUE THE PLATFORM WILL DELIVER ================= */}
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Value the Platform will Deliver
            </span>

            {renderBlockRepeater(
              "Value Items",
              values,
              setValues,
              "ValueIndex",
              { placeholder: "e.g. Monthly strategic syncs and regulatory updates" }
            )}
          </Card>

          {/* ================= MEDIA ================= */}
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Media
            </span>

            <Form.Group className="mb-2" controlId="logo">
              <Form.Label className="label">Logo</Form.Label>
              <p className="text-primary mt-1" style={{ fontSize: "13px" }}>
                Note: Please upload an image of size:{" "}
                <strong>202 × 95 px</strong> in{" "}
                <strong>.png, .jpeg, .jpg or .svg</strong> format.
              </p>
              <Form.Control
                type="file"
                accept="image/*"
                className="custom-image-upload"
                onChange={(e) => {
                  const input = e.target as HTMLInputElement;
                  const file = input.files && input.files[0];
                  if (file) setLogo(file);
                }}
              />
            </Form.Group>

            {(logo || data?.logo) && (
              <div className="mb-2 event-image">
                <img
                  src={logo ? URL.createObjectURL(logo) : data?.logo}
                  alt="Logo"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 200,
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
          </Card>

          {/* ================= SEO ================= */}
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              SEO
            </span>

            <Form.Label className="label">
              Meta Description <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              placeholder="Meta Description"
              {...register("meta_description", {
                required: "Meta description is required",
              })}
            />
            {errors.meta_description && (
              <div className="error-message">
                {errors.meta_description.message}
              </div>
            )}

            <Form.Group className="mb-2 mt-2" controlId="keyword">
              <Form.Label className="label">
                Keywords <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter keywords"
                {...register("keyword", { required: "Keywords are required" })}
              />
              {errors.keyword && (
                <div className="error-message">{errors.keyword.message}</div>
              )}
            </Form.Group>
          </Card>
        </Form>
      </Offcanvas.Body>

      <div className="d-flex justify-content-end p-3">
        <CustomButton
          isLoading={isLoading}
          label="Save"
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddBessForCI;
